import { friendlyDbError, isForeignKeyViolation } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { getCachedClientStates, getCachedLeadStates, referenceCache, getCurrentUserOrganizationId } from './referenceDataCache';

export interface ClientState {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  color: string;
  is_default: boolean;
  is_active: boolean;
  is_lead: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface LeadState {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  color: string;
  is_default: boolean;
  is_active: boolean;
  is_won?: boolean; // Stato rappresenta una conversione riuscita (lead vinto)
  is_lost?: boolean; // Stato rappresenta una conversione fallita (lead perso)
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface LeadSource {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface StateAutomation {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  note_template?: string;
  trigger_type: string;
  trigger_conditions?: any;
  source_type: 'lead' | 'client';
  source_state_id?: string;
  target_type: 'lead' | 'client';
  target_state_id?: string;
  target_source_id?: string;
  create_note?: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface CustomStatesStore {
  clientStates: ClientState[];
  leadStates: LeadState[];
  leadSources: LeadSource[];
  automations: StateAutomation[];
  loading: boolean;
  error: string | null;

  // Client States
  fetchClientStates: () => Promise<void>;
  createClientState: (state: Omit<ClientState, 'id' | 'organization_id' | 'created_at' | 'updated_at'>) => Promise<ClientState | null>;
  updateClientState: (id: string, updates: Partial<ClientState>) => Promise<ClientState | null>;
  deleteClientState: (id: string) => Promise<'deleted' | 'deactivated' | null>;
  setDefaultClientState: (id: string) => Promise<void>;

  // Lead States  
  fetchLeadStates: () => Promise<void>;
  createLeadState: (state: Omit<LeadState, 'id' | 'organization_id' | 'created_at' | 'updated_at'>) => Promise<LeadState | null>;
  updateLeadState: (id: string, updates: Partial<LeadState>) => Promise<LeadState | null>;
  deleteLeadState: (id: string) => Promise<void>;
  setDefaultLeadState: (id: string) => Promise<void>;

  // Lead Sources
  fetchLeadSources: () => Promise<void>;
  createLeadSource: (source: Omit<LeadSource, 'id' | 'organization_id' | 'created_at' | 'updated_at'>) => Promise<LeadSource | null>;
  updateLeadSource: (id: string, updates: Partial<LeadSource>) => Promise<LeadSource | null>;
  deleteLeadSource: (id: string) => Promise<void>;
  reorderClientStates: (items: ClientState[]) => Promise<void>;
  reorderLeadStates: (items: LeadState[]) => Promise<void>;
  reorderLeadSources: (items: LeadSource[]) => Promise<void>;

  // Automations
  fetchAutomations: () => Promise<void>;
  createAutomation: (automation: Omit<StateAutomation, 'id' | 'organization_id' | 'created_at' | 'updated_at'>) => Promise<StateAutomation | null>;
  updateAutomation: (id: string, updates: Partial<StateAutomation>) => Promise<StateAutomation | null>;
  deleteAutomation: (id: string) => Promise<void>;

  // Utility functions
  getDefaultClientState: () => ClientState | null;
  getDefaultLeadState: () => LeadState | null;
  executeAutomations: (triggerType: string, entityId: string, entityType: 'lead' | 'client', data?: any) => Promise<void>;
}

export const useCustomStatesStore = create<CustomStatesStore>((set, get) => ({
  clientStates: [],
  leadStates: [],
  leadSources: [],
  automations: [],
  loading: false,
  error: null,

  fetchClientStates: async () => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization ID from cache
      const organizationId = await getCurrentUserOrganizationId();

      // Use cached data for better performance
      const clientStates = await getCachedClientStates(organizationId);
      set({ clientStates });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  createClientState: async (stateData) => {
    set({ error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profileData?.organization_id) throw new Error('Organization not found');

      const { data, error } = await supabase
        .from('client_states')
        .insert({
          ...stateData,
          organization_id: profileData.organization_id
        })
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        clientStates: [...state.clientStates, data].sort((a, b) => a.sort_order - b.sort_order)
      }));

      // Clear cache to ensure fresh data on next fetch
      referenceCache.clear('client_states', profileData.organization_id);

      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updateClientState: async (id, updates) => {
    set({ error: null });
    try {
      const { data, error } = await supabase
        .from('client_states')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        clientStates: state.clientStates.map(s => s.id === id ? data : s)
      }));

      // Clear cache to ensure fresh data on next fetch
      referenceCache.clear('client_states');

      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteClientState: async (id) => {
    set({ error: null });
    try {
      const { error } = await supabase
        .from('client_states')
        .delete()
        .eq('id', id);

      if (error) {
        // Lo stato è referenziato dallo storico (client_state_changes, FK RESTRICT):
        // non può essere eliminato fisicamente, quindi viene disattivato.
        if (isForeignKeyViolation(error)) {
          const { error: deactivateError } = await supabase
            .from('client_states')
            .update({ is_active: false, is_default: false })
            .eq('id', id);

          if (deactivateError) throw deactivateError;

          set(state => ({
            clientStates: state.clientStates.map(s => s.id === id ? { ...s, is_active: false, is_default: false } : s)
          }));
          referenceCache.clear('client_states');
          return 'deactivated';
        }
        throw error;
      }

      set(state => ({
        clientStates: state.clientStates.filter(s => s.id !== id)
      }));

      // Clear cache to ensure fresh data on next fetch
      referenceCache.clear('client_states');
      return 'deleted';
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  setDefaultClientState: async (id) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profileData?.organization_id) throw new Error('Organization not found');

      // Reset all states to not default
      await supabase
        .from('client_states')
        .update({ is_default: false })
        .eq('organization_id', profileData.organization_id);

      // Set the selected state as default
      const { data, error } = await supabase
        .from('client_states')
        .update({ is_default: true })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Refresh the list
      get().fetchClientStates();
    } catch (error) {
      set({ error: friendlyDbError(error) });
    }
  },

  fetchLeadStates: async () => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization ID from cache
      const organizationId = await getCurrentUserOrganizationId();

      // Use cached data for better performance
      const leadStates = await getCachedLeadStates(organizationId);
      set({ leadStates });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  createLeadState: async (stateData) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profileData?.organization_id) throw new Error('Organization not found');

      const { data, error } = await supabase
        .from('lead_states')
        .insert({
          ...stateData,
          organization_id: profileData.organization_id
        })
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        leadStates: [...state.leadStates, data].sort((a, b) => a.sort_order - b.sort_order)
      }));

      // Clear cache to ensure fresh data on next fetch
      referenceCache.clear('lead_states');

      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updateLeadState: async (id, updates) => {
    try {
      const { data, error } = await supabase
        .from('lead_states')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        leadStates: state.leadStates.map(s => s.id === id ? data : s)
      }));

      // Clear cache to ensure fresh data on next fetch
      referenceCache.clear('lead_states');

      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteLeadState: async (id) => {
    try {
      const { error } = await supabase
        .from('lead_states')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        leadStates: state.leadStates.filter(s => s.id !== id)
      }));

      // Clear cache to ensure fresh data on next fetch
      referenceCache.clear('lead_states');
    } catch (error) {
      set({ error: friendlyDbError(error) });
    }
  },

  setDefaultLeadState: async (id) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profileData?.organization_id) throw new Error('Organization not found');

      // Reset all states to not default
      await supabase
        .from('lead_states')
        .update({ is_default: false })
        .eq('organization_id', profileData.organization_id);

      // Set the selected state as default
      const { data, error } = await supabase
        .from('lead_states')
        .update({ is_default: true })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Refresh the list
      get().fetchLeadStates();
    } catch (error) {
      set({ error: friendlyDbError(error) });
    }
  },

  fetchLeadSources: async () => {
    set({ loading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profileData?.organization_id) throw new Error('Organization not found');

      const { data, error } = await supabase
        .from('lead_sources')
        .select('id, name, description, category, is_active, sort_order, created_at, updated_at')
        .eq('organization_id', profileData.organization_id)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      set({ leadSources: data || [] });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  createLeadSource: async (sourceData) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profileData?.organization_id) throw new Error('Organization not found');

      const { data, error } = await supabase
        .from('lead_sources')
        .insert({
          ...sourceData,
          organization_id: profileData.organization_id
        })
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        leadSources: [...state.leadSources, data].sort((a, b) => a.sort_order - b.sort_order)
      }));

      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updateLeadSource: async (id, updates) => {
    try {
      const { data, error } = await supabase
        .from('lead_sources')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        leadSources: state.leadSources.map(s => s.id === id ? data : s)
      }));

      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteLeadSource: async (id) => {
    try {
      const { error } = await supabase
        .from('lead_sources')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        leadSources: state.leadSources.filter(s => s.id !== id)
      }));
    } catch (error) {
      set({ error: friendlyDbError(error) });
    }
  },

  fetchAutomations: async () => {
    set({ loading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profileData?.organization_id) throw new Error('Organization not found');

      const { data, error } = await supabase
        .from('state_automations')
        .select('*')
        .eq('organization_id', profileData.organization_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ automations: data || [] });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  createAutomation: async (automationData) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profileData?.organization_id) throw new Error('Organization not found');

      const { data, error } = await supabase
        .from('state_automations')
        .insert({
          ...automationData,
          organization_id: profileData.organization_id
        })
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        automations: [data, ...state.automations]
      }));

      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updateAutomation: async (id, updates) => {
    try {
      const { data, error } = await supabase
        .from('state_automations')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        automations: state.automations.map(a => a.id === id ? data : a)
      }));

      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteAutomation: async (id) => {
    try {
      const { error } = await supabase
        .from('state_automations')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        automations: state.automations.filter(a => a.id !== id)
      }));
    } catch (error) {
      set({ error: friendlyDbError(error) });
    }
  },

  getDefaultClientState: () => {
    const { clientStates } = get();
    return clientStates.find(state => state.is_default) || clientStates[0] || null;
  },

  getDefaultLeadState: () => {
    const { leadStates } = get();
    return leadStates.find(state => state.is_default) || leadStates[0] || null;
  },

  reorderClientStates: async (items) => {
    try {
      // Update sort_order for each item
      const updates = items.map((item, index) =>
        supabase
          .from('client_states')
          .update({ sort_order: index })
          .eq('id', item.id)
          .select()
      );

      await Promise.all(updates);

      // Update local state
      set({ clientStates: items });

      // Clear cache to ensure fresh data on next fetch
      referenceCache.clear('client_states');
    } catch (error) {
      set({ error: friendlyDbError(error) });
    }
  },

  reorderLeadStates: async (items) => {
    try {
      // Update sort_order for each item
      const updates = items.map((item, index) =>
        supabase
          .from('lead_states')
          .update({ sort_order: index })
          .eq('id', item.id)
          .select()
      );

      await Promise.all(updates);

      // Update local state
      set({ leadStates: items });

      // Clear cache to ensure fresh data on next fetch
      referenceCache.clear('lead_states');
    } catch (error) {
      set({ error: friendlyDbError(error) });
    }
  },

  reorderLeadSources: async (items) => {
    try {
      // Update sort_order for each item
      const updates = items.map((item, index) =>
        supabase
          .from('lead_sources')
          .update({ sort_order: index })
          .eq('id', item.id)
          .select()
      );

      await Promise.all(updates);

      // Update local state
      set({ leadSources: items });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    }
  }
}));